import React, {
  ChangeEvent,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import clsx from "clsx";
import OctTableCollapseRow from "./OctTableCollapseRow";
import { CustomElementbyTagName } from "./OctCustomElementbyTagName";
import {DELETE_TYPE, SELECTION_MODE, TABLE_FIELD, TYPE} from "../../../utils/Constant";
import { columnNamesTypeTableCollapse } from "@oceantech/oceantech-ui";
import { ConfirmDialog } from "../../ConfirmDialog";
import OCTActionTableTab from "../components/OCTActionTableTab";
import CustomIconButton from "../../custom-icon-button/CustomIconButton";
import {AppContext} from "../../../appContext/AppContext";
import { handleKeyDown, handleKeyUp } from "../../../utils/AppFunction";

export type CellType = TYPE.TEXT | TYPE.NUMBER | TYPE.MONEY | TYPE.DATE;

export interface fieldParentProps {
  field: string;
  label?: string;
  type?: TYPE;
}

export interface fieldGroupByLevel {
    level: number;
    field: fieldParentProps[];
}

export interface TableProps {
  data: any[];
  columns: columnNamesTypeTableCollapse[];
  headerClasses?: string;
  bodyClasses?: string;
  name?: string;
  nameParent?: string;
  height?: number;
  scrollable?: boolean;
  nameChildren: string;
  titleComponent?: string;
  setData: (data: any) => void;
  selectData: (data: any) => void;
  selectionMode?: (typeof SELECTION_MODE)[keyof typeof SELECTION_MODE];
  isSelect?: boolean;
  notDelete?: boolean;
  handleDeleteList?: (ids: any) => void;
  title?: string;
  buttonAdd?: boolean;
  handleOpenDialog?: () => void;
  noToolbar?: boolean;
  uniquePrefix?: string;
  disabledSelect?: boolean;
  handleDoubleClick?: (row: any) => void;
  buttonDelete?: boolean;
  buttonEdit?: boolean;
  buttonView?: boolean;
  actionColumnComponent?: React.ReactNode;
  handleDelete?: (row: any) => void;
  handleEdit?: (row: any) => void;
  handleView?: (row: any) => void;
  actionStyle?: React.CSSProperties;
  searchObject: object;
  setSearchObject?: any;
  isSearchable?: boolean;
  updateTableData?: () => void;
  isLevel?: boolean;
  noAction?: boolean;
  isOneLevelCheckBox?: boolean;
  customFieldParent?: fieldParentProps[];
  fieldGroupByLevel?: fieldGroupByLevel[];
  handleSetShowCheckBox?: (row: any) => boolean;
}

const CustomTable: FC<TableProps> = (props) => {
  const {
    data,
    nameChildren,
    nameParent,
    headerClasses,
    bodyClasses,
    columns,
    height,
    scrollable,
    setData,
    selectionMode,
    isSelect,
    notDelete,
    handleDelete,
    title,
    buttonAdd,
    handleOpenDialog,
    noToolbar,
    uniquePrefix = "id",
    disabledSelect,
    handleDoubleClick,
    handleDeleteList,
    actionStyle,
    isSearchable,
    updateTableData,
    searchObject,
    setSearchObject,
    isLevel,
    noAction,
    isOneLevelCheckBox,
    customFieldParent,
    fieldGroupByLevel,
    handleSetShowCheckBox,
  } = props;

  const { setIsLoading } = useContext(AppContext);

  const [itemList, setItemList] = useState<any[]>([]);
  const [dataList, setDataList] = useState<any[]>([]);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [shouldOpenConfirmDeleteDialog, setShouldOpenConfirmDeleteDialog] =
    useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [deleteType, setDeleteType] = useState<number>(0);
  const [currentRowData, setCurrentRowData] = useState<any>(null);

  const isRenderCollapseIcon = data?.some(
    (item: any) => item[nameChildren]?.length > 0
  );

  const handleCheckBox = (
    event: ChangeEvent<HTMLInputElement>,
    item: any,
    parentIndex: number
  ) => {
    const { checked } = event.target;
    const updatedData = updateCheckedSingle(checked, data, item, parentIndex);
    const updatedItem = updateCheckedSingle(
      checked,
      itemList,
      item,
      parentIndex
    );

    setAfterDataChecked(updatedData, updatedItem);
  };

  const handleCheckBoxAll = (
    event: ChangeEvent<HTMLInputElement>,
    index: any
  ) => {
    const { checked } = event.target;
    const updatedData = updateCheckedAll(data, checked, index);
    const updatedItem = updateCheckedAll(itemList, checked, index);

    setAfterDataChecked(updatedData, updatedItem);
  };

  const updateCheckedAll = (
    data: any[],
    isParentChecked: boolean,
    rowIndex: any
  ) => {
    return data.map((item, index) => {
      return index === rowIndex
        ? { ...updateChildren(data[index], isParentChecked), isParentChecked }
        : item;
    });
  };

  const setAfterDataChecked = (updatedData: any, updatedItemList: any) => {
    setData(updatedData);
    setItemList(updatedItemList);
    setDataList(updatedData);
    checkAllItemChecked(updatedData);

    const checkedItems = getCheckedItems(updatedData);
    props?.selectData(checkedItems);
  };

  const updateCheckedSingle = (
      isChecked: boolean,
      data: any[],
      currentItem: any,
      parentIndex: number
  ) => {
    const updateSingleItem = (items: any[]): any[] => {
      return items.map((item) => {
        if (item[uniquePrefix] === currentItem[uniquePrefix]) {
          return { ...item, isChecked: isChecked };
        } else if (item[nameChildren]?.length) {
          return {
            ...item,
            [nameChildren]: updateSingleItem(item[nameChildren]),
          };
        } else {
          return item;
        }
      });
    };

    return data.map((row: any, index: number) => {
      if (index === parentIndex) {
        return {
          ...row,
          [nameChildren]: row[nameChildren]
              ? updateSingleItem(row[nameChildren])
              : [],
          isChecked:
              row[uniquePrefix] === currentItem[uniquePrefix]
                  ? isChecked
                  : row.isChecked,
        };
      }
      return row;
    });
  };

  useEffect(() => {
    checkAllItemChecked(data);
  }, [data]);

  const updateChildren = (items: any, isChecked: boolean) => {
    if (items?.[nameChildren]?.length > 0) {
      items?.[nameChildren]?.forEach((item: any) => {
        item.isChecked = isChecked;
        updateChildren(item, isChecked);
      });
    }
    return items;
  };

  const getCheckedItems = useCallback(
    (data: any[]): any[] => {
      let checkedItems: any[] = [];

      const traverse = (item: any) => {
        if (item.isChecked) {
          checkedItems.push(item);
        }

        if (item?.[nameChildren] && item?.[nameChildren].length > 0) {
          item?.[nameChildren].forEach((child: any) => traverse(child));
        }
      };

      if (data) {
        data.forEach((item: any) => traverse(item));
      }
      return checkedItems;
    },
    [nameChildren]
  );

  useEffect(() => {
    if (selectionMode === SELECTION_MODE.SINGLE) {
      setDataTable(dataList);
      const checkedItems = getCheckedItems(dataList);
      props?.selectData(checkedItems[0]);
    } else if (selectionMode === SELECTION_MODE.MULTI) {
      setDataTable(itemList);
      const checkedItems = getCheckedItems(itemList);
      props?.selectData(checkedItems);
    } else {
      setDataTable(data);
    }
  }, [data, dataList, itemList, selectionMode, getCheckedItems]);

  const styles: object = {
    height: height,
    overflowY: scrollable && "auto",
  };

  const checkSearch = useCallback(() => {
    let check = Object.values(searchObject)?.some((value: any) => value);
    return check;
  }, [searchObject]);

  useEffect(() => {
    // if (!checkSearch()) {
      setItemList(data);
      setDataList(data);
    // }
  }, [data, checkSearch]);

  const listItemService = (item: any) => {
    const newItem = { ...item };
    if (newItem?.[nameChildren]) {
      newItem[nameChildren] = newItem?.[nameChildren].map((service: any) =>
        listItemService(service)
      );
    }
    return newItem;
  };

  const handleSingleSelect = (row: any, parentIndex: number) => {
    const checked = row.isChecked;
    if (checked) {
      setDataList(data);
    } else {
      const updatedData = updateCheckedSingle(true, data, row, parentIndex);
      checkAllItemChecked(updatedData);
      setDataList(updatedData);
    }
  };

  const toggleRowSelection = (row: any, parentIndex: number) => {
    const checked = row.isChecked;
    const updatedItem = updateCheckedSingle(
      !checked,
      itemList,
      row,
      parentIndex
    );
    checkAllItemChecked(updatedItem);
    setItemList(updatedItem);
  };

  const handleSwitchAllChecked = (data: any[], checkedSwitch: boolean) => {
    return data.map((item) => {
      return {
        ...updateChildren(item, checkedSwitch),
        isParentChecked: checkedSwitch,
        isChecked: checkedSwitch,
      };
    });
  };

  const handleUnCheckBoxAll = () => {
    const updatedData = handleSwitchAllChecked(data, false);
    const updatedItem = handleSwitchAllChecked(itemList, false);

    setAfterDataChecked(updatedData, updatedItem);
  };

  const handleCheckAll = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setIsCheckAll(checked);
    const updatedData = handleSwitchAllChecked(data, checked);
    const updatedItem = handleSwitchAllChecked(itemList, checked);

    setAfterDataChecked(updatedData, updatedItem);
  };

  const checkedItem = useMemo(() => {
    return getCheckedItems(itemList);
  }, [itemList]);

  const checkAllItemChecked = (dataChecked: any[]) => {
    setIsCheckAll(
      dataChecked
        .map((item: any) => item[nameChildren])
        .flat()
        .every((item: any) => item?.isChecked)
    );
  };

  const handleShowConfirmDialog = (type: number, rowData?: any) => {
    setDeleteType(type);
    if (DELETE_TYPE.DELETE_ALL === type) {
    } else if (type === DELETE_TYPE.SINGLE_DELETE) {
      setCurrentRowData(rowData);
    }
    setShouldOpenConfirmDeleteDialog(true);
  };

  const handleConfirmedDelete = async () => {
    try {
      if (deleteType === DELETE_TYPE.DELETE_ALL) {
        let ids = getCheckedItems(itemList)
          ?.map((row) => row?.[uniquePrefix])
          ?.toString();
        handleDeleteList && handleDeleteList(ids);
        setShouldOpenConfirmDeleteDialog(false);
      } else if (deleteType === DELETE_TYPE.SINGLE_DELETE) {
        handleDelete?.(currentRowData);
      }
    } catch {
    } finally {
      setIsLoading(false);
      setShouldOpenConfirmDeleteDialog(false);
    }
  };

  const handleSearch = () => {
    setSearchObject({
      pageIndex: 0,
      ...searchObject,
    });
    updateTableData?.();
  };

  const handleKeyDownEnterSearch = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    handleKeyDown(event, handleSearch);
  };
  const handleKeyUpSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleKeyUp(event, handleSearch);
  };

  const handleChangeValueInput = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchObject({
      ...searchObject,
      [event.target.name]: event.target.value,
    });
  };

  const actionsColumn: columnNamesTypeTableCollapse[] = [
    {
      name: "Thao tác",
      field: TABLE_FIELD.ACTION,
      isVisible: Boolean(
        props.buttonDelete || props.buttonEdit || props.actionColumnComponent
      ),
      headerStyle: {
        ...actionStyle,
        minWidth: "75px",
        width: actionStyle?.width || 100,
      },
      cellStyle: {
        ...actionStyle,
        width: actionStyle?.width || 100,
      },
      render: (rowData: any) => (
        <div className="flex flex-center flex-middle gap-3">
          {props.buttonEdit &&
          <CustomIconButton
              variant="edit"
              onClick={() => props.handleEdit?.(rowData)}
          >
            <i className="bi bi-pencil-square text-navy fs-0 text-primary"></i>
          </CustomIconButton>}
          {props.buttonDelete &&
          <CustomIconButton
              variant="delete"
              onClick={() =>
                  handleShowConfirmDialog(DELETE_TYPE.SINGLE_DELETE, rowData)
              }
          >
            <i className="bi bi-trash3-fill text-danger fs-0"></i>
          </CustomIconButton>}
          {props.buttonView &&
          <CustomIconButton
              variant="edit"
              onClick={() => props.handleView?.(rowData)}
          >
            <i className="bi bi-eye-fill text-navy fs-0"></i>
          </CustomIconButton>}
          {props.actionColumnComponent}
        </div>
      ),
    },
  ];

  const mergedColumns = [...(noAction ? [] : actionsColumn), ...columns];

  return (
    <>
      {!noToolbar && (
        <div>
          <OCTActionTableTab
            title={title}
            buttonAdd={buttonAdd}
            handleCheckBoxAll={handleUnCheckBoxAll}
            selectedRows={checkedItem}
            notDelete={notDelete}
            setShouldOpenConfirmDeleteDialog={setShouldOpenConfirmDeleteDialog}
            handleOpenDialog={handleOpenDialog}
            handleShowConfirmDialog={handleShowConfirmDialog}
            isSearchable={isSearchable}
            searchObject={searchObject}
            handleSearch={handleSearch}
            handleKeyUpSearch={handleKeyUpSearch}
            handleKeyDownEnterSearch={handleKeyDownEnterSearch}
            handleChangeTextSearch={handleChangeValueInput}
          />
        </div>
      )}
      <div className="table-responsive customs-collapse-row m-0" style={styles}>
        <table className="dataTable table w-100 p-0 py-2 border" id="kt_table_users">
          <thead
            className={clsx(
              headerClasses,
              "position-sticky top-0 z-index-1 border"
            )}
          >
            <tr className="text-white fw-bolder fs-7 text-capitalize-first gs-0 bg-header-table text-black">
              {(isSelect || isRenderCollapseIcon) && (
                <th className="cell-action cell-action position-sticky start-0 bg-header-table">
                  {/*{isSelect && (*/}
                  {/*  <Form.Check*/}
                  {/*    className="checkBox"*/}
                  {/*    checked={isCheckAll}*/}
                  {/*    onChange={(event) =>*/}
                  {/*      !disabledSelect && handleCheckAll(event)*/}
                  {/*    }*/}
                  {/*    disabled={disabledSelect}*/}
                  {/*  />*/}
                  {/*)}*/}
                </th>
              )}
              {mergedColumns?.map((column: columnNamesTypeTableCollapse, index: number) => (
                <th
                  key={index}
                  className={clsx(
                    "text-center p-table",
                    !column?.headerStyle && "min-w-60px"
                  )}
                  style={column?.headerStyle}
                >
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={clsx(bodyClasses)}>
            {dataTable?.length > 0 ? (
              dataTable?.map((row: any, index: number) => {
                let level = 0;
                return (
                  <OctTableCollapseRow
                      row={row}
                      key={index}
                      index={index}
                      nameParent={nameParent && nameParent}
                      nameChildren={nameChildren}
                      columns={mergedColumns}
                      handleCheckBox={handleCheckBox}
                      selectionMode={selectionMode && selectionMode}
                      handleSingleSelect={handleSingleSelect}
                      toggleRowSelection={toggleRowSelection}
                      handleCheckBoxAll={handleCheckBoxAll}
                      parentIndex={index}
                      isSelect={isSelect}
                      disabledSelect={disabledSelect}
                      handleDoubleClick={handleDoubleClick}
                      isRenderCollapseIcon={isRenderCollapseIcon} // todo: check if one row has children, another will row adds action box
                      level={level}
                      isOneLevelCheckBox={isOneLevelCheckBox}
                      fieldGroupByLevel={fieldGroupByLevel}
                      customFieldParent={customFieldParent}
                      handleSetShowCheckBox={handleSetShowCheckBox}
                      isLevel={isLevel} />
                );
              })
            ) : (
              <tr>
                <td className="text-center" colSpan={columns?.length + 1}>
                  {"Không có dữ liệu"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {shouldOpenConfirmDeleteDialog && (
          <ConfirmDialog
            show={shouldOpenConfirmDeleteDialog}
            title={"Xác nhận xóa"}
            message={"Bạn có muốn xóa không?"}
            yes={"Xác nhận"}
            onYesClick={handleConfirmedDelete}
            cancel={"Hủy"}
            onCancelClick={() => setShouldOpenConfirmDeleteDialog(false)}
          />
        )}
      </div>
    </>
  );
};

const OCTTableCollapse: FC<TableProps> = (props: TableProps) => {
  const { name, titleComponent } = props;
  const titleComponentType = (
    titleComponent ? titleComponent : "h1"
  ) as keyof JSX.IntrinsicElements;

  return (
    <div>
      {name && (
        <CustomElementbyTagName tagName={titleComponentType} content={name} />
      )}
      <CustomTable {...props} />
    </div>
  );
};

export default OCTTableCollapse;
